import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../app/store'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { Calendar } from 'lucide-react'
import RangeCalendar from '../orders/RangeCalendar'

interface ProfileDateButtonProps {
  selectedDateRange: {
    startDate: Date | null
    endDate: Date | null
  } | null
  onApply: (startDate: Date, endDate: Date | null) => void
  onClear: () => void
  height?: number
  iconData?: React.ReactNode
  size?: number
}

const ProfileDateButton: React.FC<ProfileDateButtonProps> = ({
  selectedDateRange,
  onApply,
  onClear,
  height = 48,
  iconData,
  size = 15,
}) => {
  const { t, i18n } = useTranslation()
  const { settings } = useSelector((state: RootState) => state.settings)
  const isRTL = settings.language === 'ar'
  const [isOpen, setIsOpen] = useState(false)
  const [localStartDate, setLocalStartDate] = useState<Date | null>(null)
  const [localEndDate, setLocalEndDate] = useState<Date | null>(null)
  const [selectingStart, setSelectingStart] = useState(true)

  // Initialize local dates when dialog opens
  useEffect(() => {
    if (isOpen) {
      setLocalStartDate(selectedDateRange?.startDate || null)
      setLocalEndDate(selectedDateRange?.endDate || null)
      setSelectingStart(!selectedDateRange?.startDate)
    }
  }, [isOpen, selectedDateRange])

  const handleDateSelect = (date: Date) => {
    if (selectingStart || !localStartDate) {
      setLocalStartDate(date)
      setLocalEndDate(null)
      setSelectingStart(false)
    } else {
      if (date >= localStartDate) {
        setLocalEndDate(date)
        setSelectingStart(true)
      } else {
        // If selected date is before start date, make it the new start date
        setLocalStartDate(date)
        setLocalEndDate(null)
      }
    }
  }

  // Get display text (matching Flutter logic)
  const getDisplayText = (): string => {
    const startDate = selectedDateRange?.startDate
    const endDate = selectedDateRange?.endDate

    if (startDate && endDate) {
      // Check if same day
      const isSameDay =
        startDate.getDate() === endDate.getDate() &&
        startDate.getMonth() === endDate.getMonth() &&
        startDate.getFullYear() === endDate.getFullYear()

      if (isSameDay) {
        // Same day: show "DD/MM"
        return `${startDate.getDate()}/${startDate.getMonth() + 1}`
      } else {
        // Range: show "DD/MM - DD/MM"
        return `${startDate.getDate()}/${startDate.getMonth() + 1} - ${endDate.getDate()}/${endDate.getMonth() + 1}`
      }
    } else if (startDate) {
      // Only start date: show "DD/MM"
      return `${startDate.getDate()}/${startDate.getMonth() + 1}`
    }

    return t('date')
  }

  const handleApply = () => {
    if (localStartDate) {
      onApply(localStartDate, localEndDate)
    }
    setIsOpen(false)
  }

  const handleClear = () => {
    onClear()
    setIsOpen(false)
  }

  const handleCancel = () => {
    setIsOpen(false)
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="bg-white border border-white rounded-xl flex items-center gap-2 px-3 py-2 hover:bg-gray-50 transition-colors"
        style={{ height: `${height}px` }}
      >
        {iconData || (
          <Calendar
            className="text-flutter-button"
            style={{ width: size, height: size }}
          />
        )}
        <span
          className="text-flutter-button font-medium font-['IBM_Plex_Sans_Arabic']"
          style={{ fontSize: size || 14 }}
        >
          {getDisplayText()}
        </span>
      </button>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleCancel}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-[600px] bg-white rounded-2xl p-5">
                  <div className="flex flex-col gap-5">
                    {/* Title */}
                    <div className="text-lg font-bold text-center font-['IBM_Plex_Sans_Arabic']">
                      {t('Select_a_time_period')}
                    </div>

                    {/* Calendar */}
                    <div className="h-[400px]">
                      <RangeCalendar
                        startDate={localStartDate}
                        endDate={localEndDate}
                        onDateSelect={handleDateSelect}
                        isRTL={isRTL}
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex justify-between items-center">
                      <button
                        onClick={handleClear}
                        className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors font-['IBM_Plex_Sans_Arabic']"
                      >
                        {t('clear_filter')}
                      </button>
                      <div className="flex gap-2">
                        <button
                          onClick={handleCancel}
                          className="px-4 py-2 text-black hover:bg-gray-100 rounded-lg transition-colors font-['IBM_Plex_Sans_Arabic']"
                        >
                          {t('common.cancel')}
                        </button>
                        <button
                          onClick={handleApply}
                          disabled={!localStartDate}
                          className="px-4 py-2 bg-flutter-button text-white rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-['IBM_Plex_Sans_Arabic']"
                        >
                          {t('common.apply')}
                        </button>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

export default ProfileDateButton

